import { t } from '@trimble/i18n';
import {
  FileFolderItem,
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import trajectoryIcon from '../../assets/icons/trajectory.svg';
import pointCloudIcon from '../../assets/icons/point-cloud.svg';

export const TRAJECTORY_ROOT_NODE_ID = 'trajectories';
export const POINT_CLOUD_ROOT_NODE_ID = 'pointClouds';
export const MODEL_ROOT_NODE_ID = 'tcModels';
export const MISSION_ROOT_NODE_ID = 'missions';

const PATH_SORT_PRIORITY = -1;

export enum MissionTreeNodeType {
  Mission = 'mission',
  Path = 'path',
  Runs = 'runs',
  Run = 'run',
}

export interface MissionTreeNodeData {
  type: MissionTreeNodeType;
  mission?: MissionItem;
  run?: MissionItem['runs'][number];
}

export const buildTrajectoryNodes = (
  items: TrajectoryItem[]
): TreeNodeDefinition<TrajectoryItem>[] => {
  const itemNodes = items
    .filter((item) => item.mission === undefined)
    .map(
      (item) =>
        ({
          id: item.id,
          label: item.name,
          data: item,
          parentId: TRAJECTORY_ROOT_NODE_ID,
          isLeaf: true,
          toggleTooltip: t(
            'mobileMapping.projectData.toggleTrajectoryVisibility'
          ),
        }) satisfies TreeNodeDefinition<TrajectoryItem>
    );

  if (itemNodes.length === 0) return [];

  return [
    {
      id: TRAJECTORY_ROOT_NODE_ID,
      label: t('mobileMapping.projectData.trajectories'),
      labelSvgIcon: trajectoryIcon,
      isLeaf: false,
    },
    ...itemNodes,
  ];
};

export const buildPointCloudNodes = (
  items: PointCloudItem[]
): TreeNodeDefinition<PointCloudItem>[] => {
  const itemNodes = items
    .filter((item) => item.mission === undefined)
    .map(
      (item) =>
        ({
          id: item.id,
          label: item.name,
          data: item,
          parentId: POINT_CLOUD_ROOT_NODE_ID,
          isLeaf: true,
          toggleTooltip: t(
            'mobileMapping.projectData.togglePointCloudVisibility'
          ),
        }) satisfies TreeNodeDefinition<PointCloudItem>
    );

  if (itemNodes.length === 0) return [];

  return [
    {
      id: POINT_CLOUD_ROOT_NODE_ID,
      label: t('mobileMapping.projectData.pointClouds'),
      labelSvgIcon: pointCloudIcon,
      isLeaf: false,
    },
    ...itemNodes,
  ];
};

export const buildModelNodes = (
  items: FileFolderItem[]
): TreeNodeDefinition<FileFolderItem>[] => {
  const itemNodes = items.map((item) => {
    const isFolder = item.type === 'FOLDER';
    return {
      id: item.id,
      label: item.name,
      data: item,
      parentId: item.rootElement ? MODEL_ROOT_NODE_ID : item.parentId,
      isLeaf: !isFolder,
      toggleTooltip: isFolder
        ? undefined
        : t('mobileMapping.projectData.toggleModelVisibility'),
    } satisfies TreeNodeDefinition<FileFolderItem>;
  });

  if (itemNodes.length === 0) return [];

  return [
    {
      id: MODEL_ROOT_NODE_ID,
      label: t('mobileMapping.projectData.models'),
      labelIcon: 'cube',
      isLeaf: false,
    },
    ...itemNodes,
  ];
};

export const buildMissionNodes = (
  items: MissionItem[]
): TreeNodeDefinition<MissionTreeNodeData>[] => {
  const nodes: TreeNodeDefinition<MissionTreeNodeData>[] = [
    {
      id: MISSION_ROOT_NODE_ID,
      label: t('mobileMapping.missions.missions'),
      labelSvgIcon: trajectoryIcon,
      isLeaf: false,
    },
  ];

  for (const mission of items) {
    nodes.push({
      id: mission.id,
      label: mission.name,
      data: { type: MissionTreeNodeType.Mission, mission },
      parentId: MISSION_ROOT_NODE_ID,
      isLeaf: false,
      dataTestId: `${MissionTreeNodeType.Mission}-tree-item`,
    });

    const runsNodeId = `runs:${mission.id}`;
    nodes.push({
      id: runsNodeId,
      label: t('mobileMapping.runs.runs') + ` (${mission.runs.length})`,
      data: { type: MissionTreeNodeType.Runs, mission },
      parentId: mission.id,
      isLeaf: false,
      dataTestId: `${MissionTreeNodeType.Runs}-tree-item`,
    });

    for (const run of mission.runs) {
      nodes.push({
        id: run.id,
        label: run.name,
        data: { type: MissionTreeNodeType.Run, mission, run },
        parentId: runsNodeId,
        isLeaf: true,
        toggleTooltip: t('mobileMapping.missions.toggleRunVisibility'),
        dataTestId: `${MissionTreeNodeType.Run}-tree-item`,
      });
    }

    if (mission.pathId == null) continue;

    nodes.push({
      id: mission.pathId,
      label: t('mobileMapping.missions.path'),
      data: { type: MissionTreeNodeType.Path, mission },
      parentId: mission.id,
      isLeaf: true,
      sortPriority: PATH_SORT_PRIORITY,
      toggleTooltip: t('mobileMapping.missions.toggleMissionVisibility'),
      dataTestId: `${MissionTreeNodeType.Path}-tree-item`,
    });
  }

  return nodes.length === 1 ? [] : nodes;
};
